'use client'

import { useEffect, useState, useRef, useCallback } from 'react'

import { toast } from 'sonner'
import { useTranslations } from 'next-intl'
import { useSocket } from '../hooks/useSocket'
import { useRouter, useSearchParams, usePathname } from 'next/navigation'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { useDisplayNames } from '@/context/display-names-context'
import { useAppSelector, useAppDispatch } from '@/store'
import { selectRoom, setActiveThread } from '@/store/features/chatSlice'
import { MessageEventData } from './types'

export const ChatNotificationListener = () => {
  const { currentUser } = useCurrentUser()
  const displayNames = useDisplayNames()
  const { socket } = useSocket(currentUser?.token || null)
  const router = useRouter()
  const searchParams = useSearchParams()
  const pathname = usePathname()
  const dispatch = useAppDispatch()
  const rooms = useAppSelector((state) => state.chat?.rooms || [])
  const t = useTranslations('Chat')

  // Get active room from URL instead of Redux
  const [activeRoomId, setActiveRoomId] = useState<number | null>(null)

  // Audio ref for notification sound
  const audioRef = useRef<HTMLAudioElement>(null)

  // Notification permission state
  const [notificationPermission, setNotificationPermission] =
    useState<NotificationPermission>('default')

  // Function to play notification sound
  const playNotificationSound = () => {
    if (audioRef.current) {
      try {
        // Reset audio to beginning and play
        audioRef.current.currentTime = 0
        audioRef.current.play().catch((error) => {
          console.warn('Could not play notification sound:', error)
        })
      } catch (error) {
        console.warn('Error playing notification sound:', error)
      }
    }
  }

  // Function to request notification permission
  const requestNotificationPermission = useCallback(async () => {
    if ('Notification' in window) {
      try {
        const permission = await Notification.requestPermission()
        setNotificationPermission(permission)
        return permission
      } catch (error) {
        console.warn('Error requesting notification permission:', error)
        return 'denied'
      }
    }
    return 'denied'
  }, [])

  // Function to show browser notification
  const showBrowserNotification = useCallback(
    async (title: string, body: string, roomId: number) => {
      console.log('🔔 Attempting to show browser notification:', {
        title,
        body,
        roomId
      })

      if (!('Notification' in window)) {
        console.log('❌ Browser does not support notifications')
        return // Browser doesn't support notifications
      }

      // Check current permission (it might have changed)
      const currentPermission = Notification.permission
      console.log(
        '🔐 Current notification permission:',
        currentPermission,
        'State:',
        notificationPermission
      )

      // Update our state if it's different
      if (currentPermission !== notificationPermission) {
        console.log(
          '🔄 Updating permission state from',
          notificationPermission,
          'to',
          currentPermission
        )
        setNotificationPermission(currentPermission)
      }

      // If permission is default, request it
      if (currentPermission === 'default') {
        console.log('❓ Permission is default, requesting...')
        const permission = await requestNotificationPermission()
        console.log('📝 Permission request result:', permission)
        if (permission !== 'granted') {
          // Show toast asking user to enable notifications
          toast('Enable browser notifications', {
            description:
              'Get notified of new messages even when the tab is in the background',
            action: {
              label: 'Enable',
              onClick: async () => {
                await requestNotificationPermission()
              }
            },
            duration: 8000
          })
          return
        }
      }

      // Show notification if permission is granted
      if (currentPermission === 'granted') {
        try {
          const notification = new Notification(title, {
            body,
            icon: 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7', // Transparent 1x1 pixel
            tag: `chat-${roomId}`, // Prevents duplicate notifications for same room
            requireInteraction: false,
            silent: false // We already have audio notification
          })

          // Close notification after 5 seconds
          setTimeout(() => {
            notification.close()
          }, 5000)

          // Handle click to open chat room
          notification.onclick = () => {
            window.focus() // Focus the browser window
            const url = new URL(window.location.href)
            url.searchParams.set('roomId', roomId.toString())
            url.searchParams.set('chatActive', 'true')
            router.push(url.toString())
            notification.close()
          }
        } catch (error) {
          console.error('❌ Error creating browser notification:', error)
        }
      } else if (currentPermission === 'denied') {
        // Show toast explaining how to enable notifications manually
        toast('Browser notifications blocked', {
          description:
            'Click the 🔒 icon in your address bar to allow notifications',
          duration: 6000
        })
      }
    },
    [notificationPermission, requestNotificationPermission, router]
  )

  // Initialize notification permission on mount
  useEffect(() => {
    if ('Notification' in window) {
      const initialPermission = Notification.permission
      console.log(
        '🔧 Initializing notifications, current permission:',
        initialPermission
      )
      setNotificationPermission(initialPermission)

      console.log(
        '✅ ChatNotificationListener initialized with permission:',
        initialPermission
      )
    } else {
      console.log('❌ Notifications not supported in this browser')
    }
  }, [showBrowserNotification])

  // Update active room ID whenever search params change
  useEffect(() => {
    const roomIdParam = searchParams.get('roomId')
    const chatActive = searchParams.get('chatActive')

    // Only set active if both roomId exists and chatActive=true
    if (roomIdParam && chatActive === 'true') {
      const roomId = Number(roomIdParam)
      setActiveRoomId(roomId)
    } else {
      setActiveRoomId(null)
    }
  }, [searchParams])

  // Listen for new messages
  useEffect(() => {
    if (!socket || !currentUser?.id) return

    // Handler for new messages
    const handleNewMessage = async (data: MessageEventData) => {
      // Extract room ID - try all possible paths
      let messageRoomId: number | null = null

      if (data.roomId !== undefined) {
        messageRoomId = Number(data.roomId)
      } else if (data.message?.room) {
        if (
          typeof data.message.room === 'object' &&
          data.message.room !== null
        ) {
          messageRoomId = Number(data.message.room.id)
        } else {
          messageRoomId = Number(data.message.room)
        }
      }

      if (!messageRoomId || isNaN(messageRoomId)) {
        return
      }

      // Don't notify for messages in the currently visible room
      if (activeRoomId === messageRoomId) {
        return
      }

      // Extract sender info
      let senderId: number | null = null
      let senderName = 'Someone'

      if (data.message?.sender) {
        senderId = Number(data.message.sender.id)
        senderName = displayNames.get(senderId, data.message.sender.username) || 'Someone'
      }

      // Don't notify for messages sent by the current user
      if (senderId === currentUser.id) {
        return
      }

      // Scope toast/push to the viewer's active agency:
      //   - support_bot + TS viewer     → notify only on bot-silent (ticketed) threads
      //   - support_bot + reporter      → notify only after TS has taken over
      //                                   (_botSilent) AND the sender is TS/bot, so
      //                                   the reporter isn't pinged during pre-ticket
      //                                   bot Q&A that they're already driving.
      //   - normal rooms                → only when the room's agency matches the viewer's.
      // Alerts/counters (topbar + switcher) are handled elsewhere and stay unchanged.
      // Bot payloads carry fields beyond the shared socket type: a _fromTsTeam flag
      // and (raw payloads) a populated parentMessage object instead of a scalar id.
      const messagePayload:
        | (Omit<MessageEventData['message'], 'parentMessage'> & {
            _fromTsTeam?: boolean
            parentMessage?: number | { id: number } | null
          })
        | undefined = data.message
      const messageRoom =
        typeof data.message?.room === 'object' ? data.message.room : undefined
      const roomKind = messageRoom?.room_kind || data.room?.room_kind
      const viewerIsTs = currentUser?.agency?.agency_type === 'TS'
      if (roomKind === 'support_bot') {
        if (!data._botSilent) return
        if (!viewerIsTs && !messagePayload?._fromTsTeam) return
      } else {
        const roomAgencyId = data.room?.agency?.id
          ?? messageRoom?.agency?.id
        const viewerAgencyId = currentUser?.agency?.id
        if (roomAgencyId && viewerAgencyId && roomAgencyId !== viewerAgencyId) {
          return
        }
      }

      // Get message content
      let messageContent = ''
      if (data.message?.content) {
        messageContent = data.message.content
      }

      // Strip @[Name] bracket syntax → @Name for user-visible previews.
      // The full bracket form stays in the stored message for rendering / search.
      const displayContent = messageContent.replace(/@\[([^\]]+)\]/g, '@$1')

      // Play notification sound
      playNotificationSound()

      // Show browser notification
      await showBrowserNotification(
        `${senderName}:`,
        displayContent.substring(0, 60) +
          (displayContent.length > 60 ? '...' : ''),
        messageRoomId
      )

      // Show toast notification
      const parentId = messagePayload?.parentMessage
      const threadParam = parentId ? (typeof parentId === 'object' ? parentId?.id : parentId) : null
      const mentionedAll = Boolean(data.mentionedAll)
      const clickAction = () => {
        // 1) Redux direkt setzen → öffnet Raum/Thread sofort in ChatApp sobald Popover sichtbar wird
        dispatch(selectRoom(messageRoomId!))
        dispatch(setActiveThread(threadParam ? Number(threadParam) : null))
        // 2) URL updaten → chatActive öffnet das Popover, roomId/threadId persistieren den State
        const params = new URLSearchParams(window.location.search)
        params.set('chatActive', 'true')
        params.set('roomId', messageRoomId!.toString())
        if (threadParam) params.set('threadId', String(threadParam))
        else params.delete('threadId')
        router.push(`${pathname}?${params.toString()}`, { scroll: false })
      }
      if (mentionedAll) {
        toast.warning(t('mentionedAllTitle', { name: senderName }), {
          description:
            displayContent.substring(0, 80) +
            (displayContent.length > 80 ? '...' : ''),
          action: { label: t('notificationViewAction'), onClick: clickAction },
          position: 'top-right',
          duration: 10000
        })
      } else {
        toast(`${senderName}:`, {
          description:
            displayContent.substring(0, 60) +
            (displayContent.length > 60 ? '...' : ''),
          action: { label: t('notificationViewAction'), onClick: clickAction },
          position: 'top-right',
          duration: 5000
        })
      }
    }

    // Register event listener for newMessage events
    socket.on('newMessage', handleNewMessage)

    // Cleanup
    return () => {
      socket.off('newMessage', handleNewMessage)
    }
  }, [socket, activeRoomId, currentUser, router, rooms, dispatch, displayNames, pathname, showBrowserNotification, t])

  // This component renders an invisible audio element for notifications
  return (
    <audio ref={audioRef} preload='auto' style={{ display: 'none' }}>
      <source src='/audio/chat_notification.wav' type='audio/wav' />
    </audio>
  )
}
